import { escapeRegExp } from './regexp'

export function removeAllLeadingSpaces(str: string): string {
  return str.replace(/^[^\S\r\n]+/gm, '')
}
export function removeAllTrailingSpaces(str: string): string {
  return str.replace(/[^\S\r\n]+$/gm, '')
}
export function removeAllLeadingAndTrailingSpaces(str: string): string {
  return removeAllLeadingSpaces(removeAllTrailingSpaces(str))
}

export function indent(str: string, options?: IndentationOptions): string
export function indent(
  str: string | undefined,
  options?: IndentationOptions,
): string | undefined
export function indent(
  str: string | undefined,
  options?: IndentationOptions,
): string | undefined {
  if (str === undefined) {
    return undefined
  }
  const { indentation, isReversed } = _getIndentationStr(options)
  if (!indentation) {
    // No indentation = no change
    return str
  }
  if (isReversed) {
    // Indenting by negative indentations just means deindenting
    return deindent(str, { using: { custom: indentation } })
  }
  let result = str.replace(/^/gm, indentation)
  if (options?.skipFirstLine) {
    // Removes the very first indentation
    result = result.substring(indentation.length)
  }
  return result
}
export function deindent(str: string, options?: IndentationOptions): string
export function deindent(
  str: string | undefined,
  options?: IndentationOptions,
): string | undefined
export function deindent(
  str: string | undefined,
  options?: IndentationOptions,
): string | undefined {
  if (str === undefined) {
    return undefined
  }
  const { indentation, isReversed } = _getIndentationStr(options)
  if (!indentation) {
    // No indentation = no change
    return str
  }
  if (isReversed) {
    // Deindenting by negative indentations just means indenting
    return indent(str, { using: { custom: indentation } })
  }
  if (options?.skipFirstLine) {
    // Adds an extra indentation at the beginning so when it is removed below,
    // we have effectively skipped the first line
    str = `${indentation}${str}`
  }
  return str.replace(new RegExp(`^${escapeRegExp(indentation)}`, 'gm'), '')
}

export interface IndentationOptions {
  /**
   * Sometimes you don't want to indent the very first line
   *
   * @default false
   */
  skipFirstLine?: boolean
  /**
   * Indicates what to indent with and how many
   *
   * @default 'tabs'
   * @example
   * // Uses tabs
   * { using: 'tabs' }
   * // =>
   * indentation = '\t'
   * // Uses spaces
   * { using: 'spaces' }
   * // =>
   * indentation = '  '
   * // Uses custom number of tabs
   * { using: { tabs: 2 } }
   * // =>
   * indentation = '\t\t'
   * // Uses custom number of spaces
   * { using: { spaces: 4 } }
   * // =>
   * indentation = '    '
   * // Uses custom indentation
   * { using: { custom: '- ' } }
   * // =>
   * indentation = '- '
   */
  using?:
    | 'tabs'
    | 'spaces'
    | number // Uses default
    | MutuallyExclusive<
        [{ tabs: number }, { spaces: number }, { custom: string }]
      >
}

function _getIndentationStr({ using = 'tabs' }: IndentationOptions = {}): {
  indentation: string
  isReversed: boolean
} {
  const tabUnit = '\t'
  const spaceUnit = ' '

  let unit: string
  let quantity: number
  if (using === 'tabs') {
    unit = tabUnit
    quantity = 1
  } else if (using === 'spaces') {
    unit = spaceUnit
    quantity = 2
  } else if (typeof using === 'number') {
    unit = tabUnit
    quantity = using
  } else {
    const { custom, spaces, tabs } = using
    if (spaces !== undefined) {
      unit = spaceUnit
      quantity = spaces
    } else if (tabs !== undefined) {
      unit = tabUnit
      quantity = tabs
    } else {
      unit = custom
      quantity = 1
    }
  }
  return {
    indentation: unit.repeat(Math.abs(quantity)),
    isReversed: quantity < 0,
  }
}
